import { formatearDia, NOMBRE_RESULTADO, type ResultadoDeHoja } from "@gestiondoc/shared";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { conAviso, useTRPC } from "../api.ts";
import type { HojaAbierta } from "../tipos.ts";
import { Aviso, Boton, PildoraDeHoja } from "./ui.tsx";

/**
 * Cómo quedó la hoja. El día de la hoja, quien la llenó lo dice al terminar: todo aceptable (se
 * cierra) o hubo no aceptables (queda por verificar). Si quedó por verificar, vuelve otro día,
 * escribe la verificación en el sitio sobre la misma hoja y pulsa "Verificación hecha". Los demás
 * ven lo que dijo, y hasta cuándo se conserva la hoja.
 */
export function ResultadoDeLaHoja({
  hoja,
  antes,
}: {
  hoja: HojaAbierta;
  /** Guarda lo pendiente antes de cambiar el estado. */
  antes?: (() => Promise<void>) | undefined;
}) {
  const trpc = useTRPC();
  const [error, setError] = useState<string | null>(null);
  const indicar = useMutation(trpc.hojas.indicarResultado.mutationOptions(conAviso(setError)));
  const verificar = useMutation(
    trpc.hojas.terminarVerificacion.mutationOptions(conAviso(setError)),
  );
  const esSuya = hoja.puedeEditar;
  const ocupado = indicar.isPending || verificar.isPending;

  async function elegir(resultado: ResultadoDeHoja) {
    await antes?.();
    indicar.mutate({ id: hoja.id, resultado });
  }
  async function cerrarVerificacion() {
    if (!window.confirm("¿La verificación en el sitio ya está escrita? La hoja se cierra.")) return;
    await antes?.();
    verificar.mutate({ id: hoja.id });
  }

  const boton = (activo: boolean, tono: string) =>
    `min-h-[44px] rounded-lg border px-4 text-[13.5px] font-bold disabled:opacity-60 ${
      activo ? tono : "border-borde bg-white text-marino hover:bg-fondo"
    }`;

  return (
    <div className="mb-4 rounded-tarjeta border border-borde bg-white px-4 py-3">
      {error && <Aviso className="mb-2">{error}</Aviso>}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-[13px] font-semibold text-marino">Cómo quedó</span>
        <PildoraDeHoja estado={hoja.estado} resultado={hoja.resultado} />
        {hoja.conservarHasta && (
          <span className="text-[12px] text-texto-3">
            Se conserva hasta el {formatearDia(hoja.conservarHasta)}
          </span>
        )}
        {esSuya && hoja.esDeHoy && (
          <div className="ml-auto flex flex-wrap gap-2">
            <button
              type="button"
              disabled={ocupado}
              aria-pressed={hoja.resultado === "aceptable"}
              onClick={() => void elegir("aceptable")}
              className={boton(
                hoja.resultado === "aceptable",
                "border-verde bg-verde-fondo text-verde",
              )}
            >
              {NOMBRE_RESULTADO.aceptable}
            </button>
            <button
              type="button"
              disabled={ocupado}
              aria-pressed={hoja.resultado === "no_aceptable"}
              onClick={() => void elegir("no_aceptable")}
              className={boton(
                hoja.resultado === "no_aceptable",
                "border-rojo bg-rojo-fondo text-rojo",
              )}
            >
              {NOMBRE_RESULTADO.no_aceptable}
            </button>
          </div>
        )}
        {esSuya && hoja.estado === "por_verificar" && (
          <Boton className="ml-auto" disabled={ocupado} onClick={() => void cerrarVerificacion()}>
            Verificación hecha
          </Boton>
        )}
      </div>
      {esSuya && hoja.esDeHoy && !hoja.resultado && (
        <p className="mt-2 text-[12px] text-texto-3">
          Al terminar la inspección, indica cómo quedó: así el auditor encuentra las hojas con no
          aceptables.
        </p>
      )}
      {esSuya && hoja.estado === "por_verificar" && (
        <p className="mt-2 text-[12px] text-rojo">
          Hubo no aceptables: cuando revises en el sitio, escribe la verificación (fecha y hora,
          observación, acción correctiva y preventiva) en esta misma hoja
          {hoja.esDeHoy ? ", hoy o cualquier día siguiente" : ""} y pulsa "Verificación hecha".
          {hoja.esDeHoy ? "" : " Lo escrito los días anteriores ya no se borra."}
        </p>
      )}
    </div>
  );
}
